import React from 'react';
import { BookOpen, Target, AlertTriangle, Info, Heart, Activity } from 'lucide-react';

/**
 * Guide Page Component - Educational content for diabetic users
 */
const GuidePage = () => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-indigo-600" />
          Guide pour les personnes diabétiques
        </h2>

        <div className="space-y-8">
          {/* Principes clés */}
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
            <h3 className="text-xl font-semibold text-indigo-800 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              🎯 Principes clés
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">1. Charge Glycémique prioritaire</h4>
                <p className="text-sm text-gray-600">
                  La CG est plus pertinente que l'IG seul car elle tient compte de la quantité 
                  de glucides réellement consommée.
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">2. Attention aux "faux amis"</h4>
                <p className="text-sm text-gray-600">
                  Des aliments à IG bas peuvent avoir une CG élevée s'ils sont riches en glucides.
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">3. La portion fait tout</h4>
                <p className="text-sm text-gray-600">
                  Même un aliment à CG élevée peut être consommé en petite quantité occasionnellement.
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">4. Combinaisons intelligentes</h4>
                <p className="text-sm text-gray-600">
                  L'association avec des protéines, fibres et graisses réduit l'impact glycémique global.
                </p>
              </div>
            </div>
          </div>

          {/* Comprendre les indices */}
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
            <h3 className="text-xl font-semibold text-blue-800 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5" />
              📊 Comprendre les indices
            </h3>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Indice Glycémique (IG)</h4>
                <p className="text-sm text-blue-700 mb-2">
                  Mesure la rapidité avec laquelle un aliment fait monter la glycémie.
                </p>
                <ul className="text-xs text-blue-600 space-y-1">
                  <li>• <strong>IG bas (&lt; 55)</strong> : Absorption lente des glucides</li>
                  <li>• <strong>IG modéré (55-70)</strong> : Absorption modérée</li>
                  <li>• <strong>IG élevé (&gt; 70)</strong> : Absorption rapide</li>
                </ul>
              </div>
              
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Charge Glycémique (CG)</h4>
                <p className="text-sm text-blue-700 mb-2">
                  Combine l'IG et la quantité de glucides dans une portion réelle.
                </p>
                <div className="text-xs text-blue-600">
                  <strong>Formule :</strong> CG = (IG × Glucides × Portion) ÷ (100 × 100)
                </div>
              </div>
            </div>
          </div>

          {/* Conseils pratiques */}
          <div className="bg-green-50 rounded-xl p-6 border border-green-200">
            <h3 className="text-xl font-semibold text-green-800 mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5" />
              💚 Conseils pratiques
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="font-medium text-green-800">✅ Aliments favorables</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Légumes verts à volonté</li>
                  <li>• Légumineuses (lentilles, haricots)</li>
                  <li>• Céréales complètes en quantité modérée</li>
                  <li>• Fruits à IG bas (pommes, poires)</li>
                  <li>• Protéines maigres</li>
                  <li>• Graisses saines (avocat, noix)</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-red-800">⚠️ À limiter</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Sucreries et confiseries</li>
                  <li>• Boissons sucrées</li>
                  <li>• Pain blanc et viennoiseries</li>
                  <li>• Pommes de terre en grande quantité</li>
                  <li>• Riz blanc raffiné</li>
                  <li>• Céréales sucrées du petit-déjeuner</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Astuces pour réduire l'impact glycémique */}
          <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
            <h3 className="text-xl font-semibold text-orange-800 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5" />
              🔧 Astuces pour réduire l'impact glycémique
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-medium text-orange-800 mb-2">Techniques culinaires</h4>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• Cuire les pâtes al dente</li>
                  <li>• Refroidir et réchauffer les féculents</li>
                  <li>• Ajouter du vinaigre aux repas</li>
                  <li>• Privilégier la cuisson vapeur</li>
                </ul>
              </div>
              
              <div className="bg-white p-4 rounded-lg">
                <h4 className="font-medium text-orange-800 mb-2">Associations d'aliments</h4>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• Commencer par les légumes et protéines</li>
                  <li>• Ajouter des fibres (son d'avoine)</li>
                  <li>• Mélanger avec des graisses saines</li>
                  <li>• Boire de l'eau plutôt que des jus</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Planification des repas */}
          <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
            <h3 className="text-xl font-semibold text-purple-800 mb-4">
              🗓️ Planification des repas
            </h3>
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-medium text-purple-800 mb-3">Exemple de répartition quotidienne</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <h5 className="font-medium text-purple-700 mb-1">🌅 Petit-déjeuner</h5>
                  <p className="text-purple-600">CG cible : 10-15</p>
                  <p className="text-xs text-purple-500 mt-1">
                    Ex: Flocons d'avoine + fruits rouges + noix
                  </p>
                </div>
                <div>
                  <h5 className="font-medium text-purple-700 mb-1">🌞 Déjeuner</h5>
                  <p className="text-purple-600">CG cible : 15-20</p>
                  <p className="text-xs text-purple-500 mt-1">
                    Ex: Salade + légumineuses + légumes + huile d'olive
                  </p>
                </div>
                <div>
                  <h5 className="font-medium text-purple-700 mb-1">🌙 Dîner</h5>
                  <p className="text-purple-600">CG cible : 10-15</p>
                  <p className="text-xs text-purple-500 mt-1">
                    Ex: Poisson + légumes verts + quinoa
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Avertissement important */}
          <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
            <h3 className="text-lg font-semibold text-amber-800 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              ⚠️ Important
            </h3>
            <p className="text-amber-700 text-sm">
              Ces informations sont à titre éducatif et ne remplacent pas les conseils de votre médecin ou nutritionniste. 
              Consultez toujours votre équipe médicale avant de modifier votre alimentation. Chaque personne diabétique 
              a des besoins spécifiques qui nécessitent un suivi médical personnalisé.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidePage;